import { Component, Input, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import {
  BusinessPartnerOption,
  BusinessPartnerRole,
  BusinessPartnerService,
} from '../../../core/services/business-partner.service';
import { AutocompleteSelectComponent } from '../autocomplete-select/autocomplete-select.component';

export type PartnerCreateRole = 'customer' | 'vendor';
export type PartnerRequiredField = 'email' | 'phone';
export type PartnerAddressType = 'contact' | 'invoice' | 'delivery' | 'other';

export interface PartnerSelectFilters {
  role?: BusinessPartnerRole;
  includeArchived?: boolean;
  identityType?: 'company' | 'person';
}

@Component({
  selector: 'app-partners-select, app-partner-autocomplete-input',
  standalone: false,
  templateUrl: './partners-select.html',
  styleUrl: './partners-select.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PartnersSelect), multi: true }],
})
export class PartnersSelect implements ControlValueAccessor {
  @ViewChild(AutocompleteSelectComponent) autocomplete?: AutocompleteSelectComponent;

  @Input() role: BusinessPartnerRole = 'all';
  @Input() filters: PartnerSelectFilters = {};
  @Input() multiple = false;
  @Input() placeholder = 'Search business partners';
  @Input() selectClass = 'form-select';
  @Input() allowCreate = true;
  @Input() createRoles: PartnerCreateRole[] | null = null;
  @Input() requiredCreateFields: PartnerRequiredField[] = [];
  @Input() requiredAddressType: PartnerAddressType | null = null;

  options: BusinessPartnerOption[] = [];
  lookupFailed = false;
  value: string | string[] = '';
  disabled = false;
  readonly search = (query: string) => this.searchPartners(query);
  readonly preload = (values: string[]) => this.preloadPartners(values);
  readonly createOptionValue = '__create_business_partner__';
  readonly pinnedCreateOptionValues = [this.createOptionValue];
  readonly noPinnedOptionValues: string[] = [];
  readonly standaloneModelOptions = { standalone: true };

  private onChange: (value: string | string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(
    private readonly partners: BusinessPartnerService,
    private readonly dialog: MatDialog,
  ) {}

  writeValue(value: string | string[] | null): void {
    this.value = value ?? (this.multiple ? [] : '');
  }

  registerOnChange(fn: (value: string | string[]) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; }

  valueChanged(value: string | string[]): void {
    const values = Array.isArray(value) ? value : [value];
    if (values.includes(this.createOptionValue)) {
      this.autocomplete?.writeValue(this.value);
      void this.openCreateModal();
      return;
    }
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  async openCreateModal(): Promise<void> {
    const { NewModal } = await import('../../../apps/configuration/contacts-partners/partners/new-modal/new-modal');
    const ref = this.dialog.open(NewModal, {
      width: this.requiredAddressType ? '880px' : '680px',
      maxWidth: '95vw',
      data: {
        role: this.effectiveRole,
        allowedRoles: this.allowedCreateRoles,
        requiredFields: this.requiredCreateFields,
        requiredAddressType: this.requiredAddressType,
        identityType: this.filters.identityType,
      },
    });
    const created = await firstValueFrom(ref.afterClosed());
    if (!created?.id) return;
    const option = { value: created.id, label: this.labelForPartner(created) };
    this.options = [...this.options.filter((item) => item.value !== option.value), option];
    const next = this.multiple
      ? [...new Set([...(Array.isArray(this.value) ? this.value : []), option.value])]
      : option.value;
    this.valueChanged(next);
  }

  private async searchPartners(query: string): Promise<BusinessPartnerOption[]> {
    try {
      const partners = await this.partners.listPartners({
        query,
        role: this.effectiveRole,
        includeArchived: !!this.filters.includeArchived,
      });
      this.lookupFailed = false;
      const options: BusinessPartnerOption[] = partners
        .filter((partner) => !this.filters.identityType ||
          (this.filters.identityType === 'person' ? partner.type === 'Individual' : partner.type !== 'Individual'))
        .slice(0, 50).map((partner) => ({
        value: partner.id,
        label: this.labelForPartner(partner),
        email: partner.email || undefined,
        region: partner.region || partner.city || undefined,
      }));
      if (this.allowCreate) {
        options.push({ value: this.createOptionValue, label: this.createOptionLabel });
      }
      return options;
    } catch {
      this.lookupFailed = true;
      return this.allowCreate
        ? [{ value: this.createOptionValue, label: this.createOptionLabel }]
        : [];
    }
  }

  private async preloadPartners(values: string[]): Promise<BusinessPartnerOption[]> {
    const records = await Promise.all([...new Set(values.filter(Boolean))].map(id => this.partners.getPartner(id)));
    return records.map(record => ({ value: record.id, label: this.labelForPartner(record),
      email: record.email || undefined, region: record.region || record.city || undefined }));
  }

  private labelForPartner(partner: { name: string; type?: string; partner_type?: string }): string {
    const person = partner.partner_type === 'person' || partner.type === 'Individual';
    return `${partner.name} (${person ? 'Person' : 'Company'})`;
  }

  private get effectiveRole(): BusinessPartnerRole {
    return this.filters.role ?? this.role;
  }

  private get allowedCreateRoles(): PartnerCreateRole[] {
    if (this.createRoles?.length) return [...new Set(this.createRoles)];
    if (this.effectiveRole === 'customer' || this.effectiveRole === 'vendor') return [this.effectiveRole];
    return ['customer', 'vendor'];
  }

  private get createOptionLabel(): string {
    const roles = this.allowedCreateRoles;
    const identity = this.filters.identityType === 'person' ? 'person' : this.filters.identityType === 'company' ? 'company' : 'business partner';
    if (roles.length === 1) return `＋ Create new ${roles[0]} ${identity}`;
    return `＋ Create new ${identity}`;
  }
}
